/**
 * Ableitungen der Preisautomatik-Übersicht (Board „Preisautomatik · Übersicht“) – nur aus
 * `dynamic-pricing/overview`: Zustand des Workspace, Kopf-Fakten (spätester Lauf je Apartment, keine
 * Workspace-Summe) und der Status je Apartment.
 */
import type { DynamicPricingApartmentOverview, DynamicPricingOverviewResponse } from '@/shared/api';

export type WorkspaceState = 'running' | 'stopped' | 'globalStopped' | 'missing';
export type Tone = 'green' | 'amber' | 'red' | 'neutral';

/** Ohne Mandanten-Steuerung rechnet der Server so wenig wie gestoppt (fail closed). */
export function workspaceState(data: DynamicPricingOverviewResponse): WorkspaceState {
  if (data.tenantControl.isMissing) return 'missing';
  if (!data.globalAutomationEnabled) return 'globalStopped';
  return data.tenantControl.isKilled ? 'stopped' : 'running';
}

export const WORKSPACE_TEXT: Record<WorkspaceState, string> = {
  running: 'läuft',
  stopped: 'gestoppt',
  globalStopped: 'global gestoppt',
  missing: 'Steuerung fehlt',
};

/** Zweiter Teil der Kopfzeile, solange nichts berechnet wird. */
export const HALTED_TEXT: Record<Exclude<WorkspaceState, 'running'>, string> = {
  stopped: 'für den ganzen Workspace gestoppt',
  globalStopped: 'global gestoppt',
  missing: 'Steuerung fehlt',
};

export const days = (count: number) => (count === 1 ? '1 Tag' : `${count} Tage`);
export const apartmentsLabel = (count: number) =>
  count === 1 ? '1 Apartment' : `${count} Apartments`;

const latest = (values: (string | null | undefined)[]) =>
  values
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null;

export function headerFacts(apartments: readonly DynamicPricingApartmentOverview[]) {
  const active = apartments.filter((apartment) => apartment.mode !== 'Off');
  const lastRun = apartments
    .filter((apartment) => apartment.lastRun)
    .sort((a, b) => b.lastRun!.completedAtUtc.localeCompare(a.lastRun!.completedAtUtc))[0];
  // Ein ausgeschaltetes Apartment behält seinen alten Zeitplan; der nächste Lauf gilt nur aktiven.
  const nextRun = active
    .map((apartment) => apartment.nextScheduledRunAtUtc)
    .filter((value): value is string => Boolean(value))
    .sort()[0];
  return {
    lastRun: lastRun
      ? {
          at: lastRun.lastRun!.completedAtUtc,
          changed: lastRun.lastRun!.changedCount,
          name: lastRun.name,
        }
      : null,
    nextRun: nextRun ?? null,
    lastConfirmed: latest(apartments.map((apartment) => apartment.cockpit.lastConfirmedAtUtc)),
    automatic: active.length,
  };
}

const requestsOpen = (pending: number) =>
  pending > 0 ? ` · ${pending} Anfrage${pending === 1 ? '' : 'n'} offen` : '';

export function tileStatus(apartment: DynamicPricingApartmentOverview): {
  tone: Tone;
  text: string;
} {
  const { requestCounts: requests, confirmedVsDesired: drift, cockpit } = apartment;
  const open = requestsOpen(requests.pending);
  if (requests.failed > 0) {
    return {
      tone: 'red',
      text: `Berechnung fehlgeschlagen · ${days(drift.daysWithDrift)} weichen ab`,
    };
  }
  if (drift.givenUpDays > 0) {
    return {
      tone: 'red',
      text: `${days(drift.givenUpDays)} aufgegeben · ${days(drift.daysWithDrift)} weichen ab`,
    };
  }
  const hasValues = cockpit.days.some(
    (day) => day.confirmedRate !== null || day.desiredRate !== null,
  );
  // Nullen ohne Lauf heißen „nichts bekannt“, nicht „alles bestätigt“.
  if (!apartment.lastRun && !hasValues) return { tone: 'neutral', text: 'Noch nicht berechnet' };
  if (drift.daysWithDrift > 0)
    return { tone: 'amber', text: `${days(drift.daysWithDrift)} weichen ab${open}` };
  if (requests.pending > 0) return { tone: 'amber', text: `Bestätigt entspricht dem Ziel${open}` };
  return { tone: 'green', text: '✓ Bestätigt entspricht dem Ziel' };
}
